import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useComparableField } from '../../context/useComparableField'
import type { ComparableValue } from '../../types/diffing'
import {
  isFieldWithCardinality,
  isNotRelevantField,
  isSubTemplatedField,
  type SubTemplateFieldValue,
  type TemplateField,
  type TemplateFieldWithCardinality,
  ValueSetupRole,
} from '../../types/templateModel'
import { isTemplateValidationErrorWithPath } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { DocumentationEntry } from '../documentationEntry/DocumentationEntry'
import { TemplateDocumentationEntry } from '../documentationEntry/TemplateDocumentationEntry'
import { EditableMarkdownViewer } from '../markdownEditor/EditableMarkdownViewer'
import { MarkdownViewer } from '../markdownviewer/MarkdownViewer'
import { parentFromSubTemplateFieldValue } from '../subTemplateParentSelector/helpers'
import { SubTemplateParentSelector } from '../subTemplateParentSelector/SubTemplateParentSelector'
import { documentationFromTemplateField } from '../templateDocumentationEditors/helpers'
import { SubtemplateVariantDocumentationEditor } from '../templateDocumentationEditors/SubtemplateVariantDocumentationEditor'
import { TemplateFieldDocumentationEditor } from '../templateDocumentationEditors/TemplateFieldDocumentationEditor'
import { expressionFromTemplateFieldWithCardinality } from '../templateFieldCardinality/helpers'
import { TemplateFieldCardinality } from '../templateFieldCardinality/TemplateFieldCardinality'
import { InheritedValueDocumentationEntry } from './InheritedValueDocumentationEntry'
import { useFieldDocumentation } from './useFieldDocumentation'

type Props = {
  field: TemplateField
  isExtendedView: boolean
  areFieldsShownAsEditable: boolean
}

const getInScopeVariant = (field: TemplateField) =>
  isSubTemplatedField(field) &&
  field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE
    ? field.value?.variants.at(0)
    : undefined

export const TemplateFieldDocumentation = ({
  field,
  isExtendedView,
  areFieldsShownAsEditable,
}: Props) => {
  const {
    values,
    modelFieldDocumentation,
    modelFieldTypeDocumentation,
    configurationFieldDocumentation,
    configurationFieldDocumentationPath,
    onlyTemplated,
  } = useFieldDocumentation(field)
  const { isDraftTemplate } = useTemplateContext()
  const translate = useTranslate()
  const { errors } = useOperationsContext()
  const isSingleSubclassSubTemplated =
    isSubTemplatedField(field) &&
    field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE

  const inScopeVariant = getInScopeVariant(field)

  const origin = isSingleSubclassSubTemplated
    ? inScopeVariant?.origin
    : field.origin
  const path =
    isSingleSubclassSubTemplated && inScopeVariant
      ? inScopeVariant?.path
      : field.path

  const templatePath = isSingleSubclassSubTemplated
    ? inScopeVariant?.path
    : field.path

  const comparableField = useComparableField<TemplateField>(field.path)
  const comparableVariant = useComparableField<SubTemplateFieldValue>(
    inScopeVariant?.path ?? '',
  )

  const documentationValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateField>) => {
      if (!comparable) {
        return ''
      }

      const documentation = documentationFromTemplateField(comparable)

      return stringifyComparableValue(documentation?.value)
    },
    [],
  )

  const variantDocumentationFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<SubTemplateFieldValue>) => {
      if (!comparable) {
        return ''
      }

      return stringifyComparableValue(comparable.documentation?.value)
    },
    [],
  )

  const parentValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateField>) => {
      if (!comparable) {
        return ''
      }

      const inScopeVariant = getInScopeVariant(comparable)
      const parent = inScopeVariant
        ? parentFromSubTemplateFieldValue(inScopeVariant)
        : undefined

      return stringifyComparableValue(parent)
    },
    [],
  )

  const cardinalityValueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<TemplateFieldWithCardinality>) => {
      if (!comparable) {
        return ''
      }

      const expression = expressionFromTemplateFieldWithCardinality(comparable)

      return stringifyComparableValue(expression)
    },
    [],
  )

  return (
    <>
      <TemplateDocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.configurationFieldDocumentation',
        )}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={documentationValueFromComparable}
            compareFilter="documentation"
          />
        }
        value={
          areFieldsShownAsEditable ? (
            <TemplateFieldDocumentationEditor field={field} />
          ) : (
            <EditableMarkdownViewer
              value={configurationFieldDocumentation?.value}
            />
          )
        }
        origin={configurationFieldDocumentation?.origin}
        path={configurationFieldDocumentationPath}
      />
      {isSingleSubclassSubTemplated &&
        isSubTemplatedField(field) &&
        inScopeVariant && (
          <TemplateDocumentationEntry
            title={translate(
              'templateViewer.fieldDetails.field.configurationVariantDocumentation',
            )}
            diffIndicator={
              inScopeVariant ? (
                <ValueSetupDiffIndicators
                  comparableValues={comparableVariant}
                  serialiseValueFn={variantDocumentationFromComparable}
                  compareFilter="variantDocumentation"
                />
              ) : undefined
            }
            value={
              areFieldsShownAsEditable ? (
                <SubtemplateVariantDocumentationEditor
                  variant={inScopeVariant}
                  forField={field}
                />
              ) : (
                <EditableMarkdownViewer
                  value={inScopeVariant?.documentation?.value}
                />
              )
            }
            origin={inScopeVariant?.documentation?.origin}
            path={`${inScopeVariant.path}.documentation`}
          />
        )}
      <DocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.modelFieldDocumentation',
        )}
        value={
          <div className="flex flex-col gap-y-4">
            {isExtendedView && (
              <div className="italic opacity-60">{templatePath}</div>
            )}
            <MarkdownViewer shouldTransformLinksToDocSite>
              {modelFieldDocumentation || '\\-'}
            </MarkdownViewer>
          </div>
        }
      />
      <DocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.modelFieldTypeDocumentation',
        )}
        value={
          <div className="flex flex-col gap-y-4">
            <MarkdownViewer shouldTransformLinksToDocSite>
              {modelFieldTypeDocumentation || '\\-'}
            </MarkdownViewer>
            {values && (
              <div className="whitespace-normal wrap-break-word text-sm italic text-gray-300">
                {JSON.stringify(values, null, 2).replaceAll('"', '')}
              </div>
            )}
          </div>
        }
      />
      {!isSingleSubclassSubTemplated && (
        <DocumentationEntry
          title={translate('templateViewer.fieldDetails.field.onlyTemplated')}
          value={onlyTemplated ? translate('true') : translate('false')}
        />
      )}
      {isSingleSubclassSubTemplated &&
        inScopeVariant &&
        isSubTemplatedField(field) && (
          <TemplateDocumentationEntry
            title={translate(
              'templateViewer.fieldDetails.field.variant.parentId',
            )}
            diffIndicator={
              <ValueSetupDiffIndicators
                comparableValues={comparableField}
                serialiseValueFn={parentValueFromComparable}
                compareFilter="parentId"
              />
            }
            value={
              isDraftTemplate &&
              !isExtendedView &&
              !isNotRelevantField(inScopeVariant.subTemplatedField) ? (
                <SubTemplateParentSelector variant={inScopeVariant} />
              ) : (
                (inScopeVariant.parent?.value ?? '-')
              )
            }
            origin={inScopeVariant.parent?.origin}
            path={`${inScopeVariant.parent}.parent`}
          />
        )}
      {isFieldWithCardinality(field) && (
        <TemplateDocumentationEntry
          isShowingErrors
          errors={errors.filter(
            ({ error }) =>
              isTemplateValidationErrorWithPath(error) &&
              error.path === `${field.origin}.cardinality`,
          )}
          title={translate(
            'templateViewer.fieldDetailsModal.field.cardinality',
          )}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={
                comparableField as ComparableValue<TemplateFieldWithCardinality>
              }
              serialiseValueFn={cardinalityValueFromComparable}
              compareFilter="cardinality"
            />
          }
          value={<TemplateFieldCardinality field={field} />}
          origin={field.value?.cardinality?.origin}
          path={`${field.path}.cardinality`}
        />
      )}
      <InheritedValueDocumentationEntry value={{ origin, path }} />
    </>
  )
}
